/**
 * Discover genres and content types (API `discover_categories.ts`).
 *
 * A listing has a genre and, separately, the kind of executable content it is.
 * Bugs is offered as a category but is a content type: a reproduction keeps
 * its own genre.
 */
export const DISCOVER_GENRE_IDS = ["apps", "games", "media", "tools", "ai"] as const;
export type DiscoverGenreId = (typeof DISCOVER_GENRE_IDS)[number];
export const DISCOVER_CATEGORY_IDS = ["apps", "games", "bugs", "media", "tools", "ai"] as const;

export type DiscoverContentType = "continue" | "bug_reproduction";

/** Publisher-declared facts. Absent fields are not shown, never inferred. */
export interface DiscoverContentDetails {
  bug?: {
    expected?: string;
    actual?: string;
    steps?: string[];
    intentional_demo: boolean;
    issue_url?: string;
  };
  continue?: { starting_state: string; base_listing_id?: string };
  media?: { interactive: true };
  tool?: {
    interface: "cli" | "terminal";
    operation?: string;
    input_format?: string;
    output_format?: string;
    usage?: string;
  };
  ai?: {
    execution: "browser" | "hosted" | "user_runner";
    model_name?: string;
    model_size?: string;
    gpu_required?: boolean;
    initial_download?: string;
  };
}

export interface DiscoverRelatedListing {
  id: string;
  title: string;
  icon: string | null;
  thumbnail_url: string | null;
  content_type: DiscoverContentType | null;
  relation: "base" | "continue";
}

/** Category ids retired with genres; old links and stored interests map onto them. */
const LEGACY_CATEGORY: Record<string, string> = {
  notes: "apps",
  organize: "apps",
  productivity: "apps",
  utilities: "tools",
};

/** A current category id, or null for an unknown one. */
export function discoverCategoryId(value: string | null | undefined): string | null {
  if (!value) return null;
  const id = LEGACY_CATEGORY[value] ?? value;
  return (DISCOVER_CATEGORY_IDS as readonly string[]).includes(id) ? id : null;
}

export type DiscoverContentTone = "bug" | "continue" | "ai" | "neutral";

interface ContentSubject {
  category?: string | null;
  contentType?: DiscoverContentType | null;
  contentDetails?: DiscoverContentDetails | null;
}

/** The thumbnail badge; ordinary apps and games have none. */
export function discoverContentBadge(
  entry: ContentSubject,
): { label: string; tone: DiscoverContentTone } | null {
  if (entry.contentType === "bug_reproduction") return { label: "Bug reproduction", tone: "bug" };
  if (entry.contentType === "continue") return { label: "Continue", tone: "continue" };
  const details = entry.contentDetails;
  if (details?.ai)
    return { label: details.ai.execution === "hosted" ? "Hosted AI" : "Local AI", tone: "ai" };
  if (details?.tool)
    return { label: details.tool.interface === "cli" ? "CLI" : "Terminal", tone: "neutral" };
  if (details?.media?.interactive) return { label: "Interactive", tone: "neutral" };
  return null;
}

/** The verb on the detail page's start button. */
export function discoverStartLabel(entry: ContentSubject): string {
  if (entry.contentType === "bug_reproduction") return "Reproduce";
  if (entry.contentType === "continue") return "Continue";
  switch (entry.category) {
    case "games":
      return "Play";
    case "media":
      return "Explore";
    case "tools":
      return "Run";
    case "ai":
      return "Try";
    default:
      return "Open";
  }
}

const isText = (value: unknown, max: number): value is string =>
  typeof value === "string" && value.trim().length > 0 && value.length <= max;

/**
 * Keep only well-formed declared facts from the API response. Anything
 * malformed is dropped rather than shown half-parsed.
 */
export function readContentDetails(value: unknown): DiscoverContentDetails | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, Record<string, unknown> | undefined>;
  const details: DiscoverContentDetails = {};
  const optional = (record: Record<string, unknown>, key: string, max: number) =>
    isText(record[key], max) ? { [key]: record[key] as string } : {};
  if (raw.bug && typeof raw.bug.intentional_demo === "boolean") {
    const steps = Array.isArray(raw.bug.steps) && raw.bug.steps.every((step) => isText(step, 300))
      ? { steps: raw.bug.steps as string[] } : {};
    let issue = {};
    if (isText(raw.bug.issue_url, 500)) {
      try {
        if (new URL(raw.bug.issue_url as string).protocol === "https:") issue = { issue_url: raw.bug.issue_url };
      } catch { /* not a link */ }
    }
    details.bug = {
      intentional_demo: raw.bug.intentional_demo,
      ...optional(raw.bug, "expected", 600),
      ...optional(raw.bug, "actual", 600),
      ...steps,
      ...issue,
    };
  }
  if (raw.continue && isText(raw.continue.starting_state, 600))
    details.continue = {
      starting_state: raw.continue.starting_state as string,
      ...optional(raw.continue, "base_listing_id", 160),
    };
  if (raw.media?.interactive === true) details.media = { interactive: true };
  if (raw.tool && (raw.tool.interface === "cli" || raw.tool.interface === "terminal"))
    details.tool = {
      interface: raw.tool.interface,
      ...optional(raw.tool, "operation", 300),
      ...optional(raw.tool, "input_format", 200),
      ...optional(raw.tool, "output_format", 200),
      ...optional(raw.tool, "usage", 600),
    };
  if (raw.ai && ["browser", "hosted", "user_runner"].includes(raw.ai.execution as string))
    details.ai = {
      execution: raw.ai.execution as "browser" | "hosted" | "user_runner",
      ...optional(raw.ai, "model_name", 160),
      ...optional(raw.ai, "model_size", 80),
      ...(typeof raw.ai.gpu_required === "boolean" ? { gpu_required: raw.ai.gpu_required } : {}),
      ...optional(raw.ai, "initial_download", 160),
    };
  return Object.keys(details).length ? details : null;
}

export function readContentType(value: unknown): DiscoverContentType | null {
  return value === "continue" || value === "bug_reproduction" ? value : null;
}
